import { Form, Modal, Table, Typography } from "antd";
import { ColumnType } from "antd/es/table";
import { useMemo, useState } from "react";
import {
  useCreateBankAccount,
  useDeleteBankAccount,
  useUpdateBankAccount,
} from "../../../../api/bankAccount";
import { ReactComponent as CloseIcon } from "../../../../common/images/svg/CloseIcon.svg";
import { ReactComponent as PencilIcon } from "../../../../common/images/svg/PencilSimpleLine.svg";
import { ReactComponent as PlusIcon } from "../../../../common/images/svg/Plus.svg";
import { ReactComponent as TrashIcon } from "../../../../common/images/svg/Trash.svg";
import { CustomButton } from "../../../../components/button/buttonCustom";
import { SearchCustom } from "../../../../components/inputs/search/searchCustom";
import ConfirmModal from "../../../../components/modal/confirmModal";
import NextPagination from "../../../../components/pagination/next";
import PrevPagination from "../../../../components/pagination/prev";
import { useNotification } from "../../../../components/providers/notification/useNotification";
import { useBankAccountManagement } from "../../../../utils/hooks/useBankAccount";
import useResponsive from "../../../../utils/hooks/useResponsive";
import { Row as RowTable } from "../../../components/interface";
import FormBank from "./form-bank";

interface IDetailInfoProps {
  tabActive: "info" | "bank" | "wallet";
  id: string | undefined;
  userId: number | undefined;
}

const start = 0;
const end = 20;

const DetailBank = ({ tabActive, id }: IDetailInfoProps) => {
  const [form] = Form.useForm();
  const { isTablet } = useResponsive();
  const { open: openNotification } = useNotification();
  const [actionBankAccount, setActionBankAccount] = useState<
    "ADD" | "EDIT" | null
  >(null);
  const [bankAccountId, setBankAccountId] = useState<number | null>(null);
  const [deleteBankAccountId, setDeleteBankAccountId] = useState<number | null>(
    null
  );
  const [currentPage, setCurrentPage] = useState(1);
  const [params, setParams] = useState({ start, end });
  const [searchName, setSearchName] = useState<string>("");

  const { dataListBankAccount, refetch, isLoading } = useBankAccountManagement(
    Number(id),
    params,
    searchName
  );

  const { mutate: createBankAccount, isLoading: isLoadingCreate } =
    useCreateBankAccount({
      onSuccess: (response: { data: { message: string } }) => {
        if (openNotification) {
          openNotification?.({
            key: "add_bank_account_success",
            message: response.data.message || "Add bank account successfully!",
            type: "success",
          });
        }
        refetch();
        setActionBankAccount(null);
        setBankAccountId(null);
        form.resetFields();
      },
      onError: (error: { message: string }) => {
        if (openNotification) {
          openNotification({
            key: "add_bank_account_failure",
            message: error?.message || "Add bank account failed!",
            type: "error",
          });
        }
      },
    });

  const { mutate: updateBankAccount, isLoading: isLoadingUpdate } =
    useUpdateBankAccount({
      onSuccess: (response: { data: { message: string } }) => {
        if (openNotification) {
          openNotification?.({
            key: "update_bank_account_success",
            message:
              response.data.message || "Update bank account successfully!",
            type: "success",
          });
        }
        refetch();
        setActionBankAccount(null);
        setBankAccountId(null);
        form.resetFields();
      },
      onError: (error: { message: string }) => {
        if (openNotification) {
          openNotification({
            key: "update_bank_account_failure",
            message: error?.message || "Update bank account failed!",
            type: "error",
          });
        }
      },
    });

  const { mutate: deleteBankAccount, isLoading: loadingDeleteWallet } =
    useDeleteBankAccount({
      onSuccess: (response: { data: { message: string } }) => {
        if (openNotification) {
          openNotification?.({
            key: "delete_bank_account_success",
            message:
              response.data.message || "Delete bank account successfully!",
            type: "success",
          });
        }
        refetch();
        setDeleteBankAccountId(null);
      },
      onError: (error: { message: string }) => {
        if (openNotification) {
          openNotification({
            key: "delete_bank_accountt_failure",
            message: error?.message || "Delete bank account failed!",
            type: "error",
          });
        }
      },
    });

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    setCurrentPage(1)
    setParams({ ...params, start, end })
  };

  const handleSearch = (value: string) => {
    setSearchName(value);
    setCurrentPage(1);
    setParams({
      start,
      end,
    });
  };

  const handleCancel = () => {
    if (isLoadingCreate || isLoadingUpdate) return;
    setActionBankAccount(null);
    setBankAccountId(null);
    form.resetFields();
  };

  const handleCancelDelete = () => {
    if (loadingDeleteWallet) return;
    setDeleteBankAccountId(null);
  };

  const handleSubmitDelete = () => {
    deleteBankAccount(deleteBankAccountId);
  };

  const onSubmitBankAccount = (values: any) => {
    const bank = !values?.bankId
      ? {
        name: values?.bankName || "",
        swift: values?.bankSwift || "",
        legalAddress: values?.bankAddress || "",
        country: values?.bankCountry || "",
      }
      : undefined;

    const body = {
      ...(values?.bankId ? { bankId: values?.bankId } : { bank }),
      currencyCode: values?.currencyCode || "",
      abaRoutingNumber: values?.abaRoutingNumber !== '' && values?.abaRoutingNumber ? values?.abaRoutingNumber : undefined,
      bankStateBranchNumber: values?.bankStateBranchNumber !== '' && values?.bankStateBranchNumber ? values?.bankStateBranchNumber : undefined,
      branchNumber: values?.branchNumber || "",
      accountNumber: values?.accountNumber || "",
      companyId: Number(id),
    };

    if (actionBankAccount === "ADD") {
      createBankAccount(body as any);
    } else {
      if (!body) return;
      updateBankAccount({
        id: bankAccountId,
        ...body,
      } as any);
    }
  };

  const columns = useMemo((): ColumnType<RowTable>[] => {
    const columns: ColumnType<RowTable>[] = [
      {
        dataIndex: "id",
        title: <span className={"column-header"}>NO.</span>,
        render: (_value, _record, index) => {
          return (
            <Typography className="text-table">
              {(dataListBankAccount.page - 1) * dataListBankAccount.pageSize +
                index +
                1}
            </Typography>
          );
        },
        onCell: (record: RowTable) => ({
          onClick: () => {
            setActionBankAccount("EDIT");
            setBankAccountId(record.id);
          },
        }),
        width: "6%",
      },
      {
        dataIndex: "accountNumber",
        title: <span className={"column-header"}>ACCT NO</span>,
        onCell: (record: RowTable) => ({
          onClick: () => {
            setActionBankAccount("EDIT");
            setBankAccountId(record.id);
          },
        }),
        width: "8.2%",
      },
      {
        dataIndex: "currencyCode",
        title: <span className={"column-header"}>CURRENCY</span>,
        onCell: (record: RowTable) => ({
          onClick: () => {
            setActionBankAccount("EDIT");
            setBankAccountId(record.id);
          },
        }),
        width: "8.2%",
      },
      {
        dataIndex: "red",
        title: <span className={"column-header"}>BANK</span>,
        onCell: (record: RowTable) => ({
          onClick: () => {
            setActionBankAccount("EDIT");
            setBankAccountId(record.id);
          },
        }),
        render: (_value, data) => {
          return (
            <Typography className="text-table">{data.bank.name}</Typography>
          );
        },

        width: "31.6%",
      },
      {
        dataIndex: "correspondentBankSwift",
        title: <span className={"column-header"}>SWIFT</span>,
        onCell: (record: RowTable) => ({
          onClick: () => {
            setActionBankAccount("EDIT");
            setBankAccountId(record.id);
          },
        }),
        render: (value, data) => {
          return (
            <Typography className="text-table">{data.bank.swift}</Typography>
          );
        },
        width: "14.4%",
      },
      {
        dataIndex: "country",
        title: <span className={"column-header"}>BANK ADDRESS</span>,
        onCell: (record: RowTable) => ({
          onClick: () => {
            setActionBankAccount("EDIT");
            setBankAccountId(record.id);
          },
        }),
        render: (value, data) => {
          return (
            <Typography className="text-table">
              {data.bank.legalAddress}
            </Typography>
          );
        },
        width: "20.4%",
      },
      {
        title: <div></div>,
        render: (record: RowTable) => (
          <div className="action-table">
            <TrashIcon
              className="icon-animation"
              onClick={() => setDeleteBankAccountId(record.id)}
            />
            <PencilIcon
              className="icon-animation"
              onClick={() => {
                setActionBankAccount("EDIT");
                setBankAccountId(record.id);
              }}
            />
          </div>
        ),
      },
    ];
    return columns.filter(({ key }) => !key);
  }, [dataListBankAccount]);

  return (
    <div
      className="wallet-container"
      style={{ display: tabActive === "bank" ? "flex" : "none" }}
    >
      <div className="wallet-header">
        <SearchCustom
          value={searchName}
          placeholder="Search"
          onChange={handleSearch}
          onSubmit={handleSearch}
        />
        <CustomButton
          size={isTablet ? "small" : "medium"}
          text="Add Bank Account"
          icon={<PlusIcon />}
          onClick={() => {
            setActionBankAccount("ADD");
            setBankAccountId(null);
          }}
        />
      </div>
      <Table
        className={`custom-ant-table custom-ant-table-wallet ${isLoading ? "table-hidden-empty" : ""
          }`}
        rowKey="id"
        rowClassName={() => "custom-table-row"}
        style={{ tableLayout: "auto" }}
        columns={columns as any}
        dataSource={dataListBankAccount.items}
        loading={isLoading}
        scroll={{ y: "calc(100vh - 345px)" }}
        pagination={{
          current: currentPage,
          total: dataListBankAccount.total,
          pageSize: dataListBankAccount.pageSize,
          showPrevNextJumpers: true,
          onChange: handlePageChange,
          itemRender: (page, type, element) => {
            switch (type) {
              case "prev":
                return (
                  <PrevPagination
                    currentPage={currentPage}
                    setCurrentPage={(page) => handlePageChange(page)}
                  />
                );
              case "next":
                return (
                  <NextPagination
                    totalPage={dataListBankAccount.totalPages}
                    currentPage={currentPage}
                    setCurrentPage={(page) => handlePageChange(page)}
                  />
                );
            }
            return element;
          },
        }}
      />
      <Modal
        open={!!actionBankAccount}
        onCancel={handleCancel}
        footer={null}
        destroyOnClose
        maskClosable={false}
        title={
          <>
            <Typography
              style={{
                fontSize: isTablet ? 16 : 20,
                fontWeight: 700,
                color: "#C2C6CE",
              }}
            >
              {actionBankAccount === "ADD"
                ? "Add Bank Account"
                : "Edit Bank Account"}
            </Typography>
            <CloseIcon onClick={handleCancel} className="icon-animation" />
          </>
        }
        className="edit-wallet-modal"
      >
        <FormBank
          form={form}
          bankAccountId={bankAccountId}
          actionBankAccount={actionBankAccount}
          handleCancel={handleCancel}
          onSubmitBankAccount={onSubmitBankAccount}
          isLoading={isLoadingCreate || isLoadingUpdate}
        />
      </Modal>
      <ConfirmModal
        type="warning"
        title="Confirm Delete"
        description="Do you want to delete this record?"
        open={!!deleteBankAccountId}
        onCancel={handleCancelDelete}
        onSubmit={handleSubmitDelete}
        isLoading={loadingDeleteWallet}
      />
    </div>
  );
};

export default DetailBank;
